import { useCallback, useEffect, useRef, useState } from "react";
import { deleteSubscription, getVapidKey, notifyErrorText, saveSubscription } from "./api";
import { isIos, isStandalone, pushSupported, urlBase64ToUint8Array } from "./pushUtils";

export const NOTIFY_SW_URL = "/notify/sw.js";
export const NOTIFY_SCOPE = "/notify/";

export type AlertsState =
  | { kind: "checking" }
  /** iOS Safari outside the installed app: push needs Add to Home Screen first. */
  | { kind: "install" }
  | { kind: "unsupported" }
  | { kind: "blocked" }
  | { kind: "off" }
  | { kind: "asking" }
  | { kind: "on" }
  /** `during` says which step failed: `enable` (the tap), `resync` (saving an
   * existing subscription again on open; the server may not have it, so alerts
   * are not shown as on) or `disable` (Turn off; the subscription still exists,
   * so the control keeps Alerts on + Turn off). */
  | { kind: "error"; message: string; during: "enable" | "resync" | "disable" };

export const RESYNC_FAILED = "Couldn't confirm alerts with the server. Please try again.";
export const TURN_OFF_FAILED = "Couldn't turn alerts off. Please try again.";

/** How long Enable waits for a just-registered worker to activate. */
export const SW_ACTIVATE_TIMEOUT_MS = 15_000;
export const SW_NOT_ACTIVE = "The alerts service didn't start. Please try again.";

const registerWorker = () =>
  navigator.serviceWorker.register(NOTIFY_SW_URL, { scope: NOTIFY_SCOPE });

/**
 * `registration` once it has an active worker (push subscribe needs one):
 * at once if it has, else when its installing or waiting worker reaches
 * `activated`. Rejects if that worker becomes redundant or `timeoutMs` passes,
 * so Enable ends in the error state (with its retry) and never stays "asking".
 */
export function whenActive(
  registration: ServiceWorkerRegistration,
  timeoutMs = SW_ACTIVATE_TIMEOUT_MS,
): Promise<ServiceWorkerRegistration> {
  if (registration.active) return Promise.resolve(registration);
  const worker = registration.installing ?? registration.waiting;
  return new Promise((resolve, reject) => {
    const finish = (ok: boolean) => {
      clearTimeout(timer);
      worker?.removeEventListener("statechange", onChange);
      if (ok) resolve(registration);
      else reject(new Error(SW_NOT_ACTIVE));
    };
    const onChange = () => {
      if (registration.active || worker?.state === "activated") finish(true);
      else if (worker?.state === "redundant") finish(false);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    worker?.addEventListener("statechange", onChange);
  });
}

const subIdKey = (siteId: string) => `notify.pushSubId.${siteId}`;

// localStorage can throw (private mode, blocked site data); the sub_id is only
// a cache, since a re-sync on open gets it back from the server.
function storedSubId(siteId: string): string | null {
  try {
    return localStorage.getItem(subIdKey(siteId));
  } catch {
    return null;
  }
}

function storeSubId(siteId: string, subId: string | null) {
  try {
    if (subId) localStorage.setItem(subIdKey(siteId), subId);
    else localStorage.removeItem(subIdKey(siteId));
  } catch {
    /* cache only */
  }
}

/** Drops this browser's push subscription without the server, so a device whose
 * server row couldn't be deleted still stops receiving: the next send to it
 * gets 404/410 and the server removes the row. Never touches the permission. */
export async function unsubscribeBrowser(): Promise<void> {
  const registration = await navigator.serviceWorker?.getRegistration(NOTIFY_SCOPE);
  await (await registration?.pushManager.getSubscription())?.unsubscribe();
}

const sameState = (a: AlertsState, b: AlertsState) =>
  a.kind === b.kind &&
  (a.kind !== "error" || (b.kind === "error" && a.message === b.message && a.during === b.during));

/**
 * This browser's Notify alerts for `siteId`: works out what the control can
 * offer, and turns alerts on (a user tap only) or off.
 *
 * On open it registers the push-only service worker (it has no fetch handler, so
 * this is safe on load) and, when permission is already granted and a
 * subscription exists, saves it again: the upsert is idempotent, keeps the
 * server row fresh if the browser rotated the endpoint, and returns the
 * `sub_id` that turning off needs. The VAPID key is fetched ahead too, so the
 * click runs requestPermission → (the registered worker active) → subscribe
 * with no network call before the prompt (WebKit may not count a prompt after
 * an await as user-initiated).
 * `onError` hears every failed Notify call (the layout refetches `/me` on a 403).
 */
export function usePushAlerts(siteId: string | null, onError?: (error: unknown) => void) {
  const [state, setState] = useState<AlertsState>({ kind: "checking" });
  // A ref, so a new callback each render doesn't re-run the effects.
  const reportError = useRef(onError);
  reportError.current = onError;
  const vapidKey = useRef<string | null>(null);
  const subId = useRef<string | null>(null);
  const registered = useRef<Promise<ServiceWorkerRegistration> | null>(null);

  useEffect(() => {
    if (!pushSupported()) {
      setState({ kind: isIos() && !isStandalone() ? "install" : "unsupported" });
      return;
    }
    registered.current = registerWorker();
    // A failed register is shown when the user taps Enable (it awaits this,
    // and registers again after a failure).
    registered.current.catch(() => {});
  }, []);

  // The re-check on return to the app (UI-02): `opSeq` is bumped when Enable or
  // Turn off starts (and on a site change / unmount), so a check that started
  // before drops its result; `busy` keeps a check from starting during them;
  // `inFlight` is the one running check (visibilitychange and focus come together).
  const current = useRef(state);
  current.current = state;
  const opSeq = useRef(0);
  const busy = useRef(false);
  const inFlight = useRef<Promise<void> | null>(null);

  /** What the browser says now (permission, subscription), with an existing
   * subscription saved again; `apply` sets the result (and drops a stale one). */
  const check = useCallback(async (site: string, apply: (next: AlertsState) => void) => {
    if (!vapidKey.current)
      getVapidKey()
        .then((key) => (vapidKey.current = key))
        .catch(() => {
          /* fetched again on enable, where its error is shown */
        });
    if (Notification.permission === "denied") return apply({ kind: "blocked" });
    const registration = await navigator.serviceWorker.getRegistration(NOTIFY_SCOPE);
    const subscription = await registration?.pushManager.getSubscription();
    if (Notification.permission !== "granted" || !subscription) return apply({ kind: "off" });
    subId.current = storedSubId(site);
    try {
      subId.current = await saveSubscription(site, subscription.toJSON());
      storeSubId(site, subId.current);
    } catch (error) {
      // The server may have lost the row, so alerts are not shown as on; the
      // retry (enable) saves the same subscription again.
      reportError.current?.(error);
      return apply({ kind: "error", message: RESYNC_FAILED, during: "resync" });
    }
    apply({ kind: "on" });
  }, []);

  /** Runs `check` on open and again each time the app comes back to the
   * foreground: the subscription or permission can change while it is in the
   * background (blocked in settings, the push service dropped it). No
   * "checking" in between, and the state is set only when it changed. An error
   * from Enable or Turn off stays on screen (its own retry); a re-sync error is
   * the same work, so it is checked again. */
  const recheck = useCallback(() => {
    if (!siteId || !pushSupported() || inFlight.current || busy.current) return;
    const shown = current.current;
    if (shown.kind === "asking" || (shown.kind === "error" && shown.during !== "resync")) return;
    const seq = opSeq.current;
    const apply = (next: AlertsState) => {
      if (opSeq.current === seq) setState((prev) => (sameState(prev, next) ? prev : next));
    };
    const running = check(siteId, apply)
      .catch((error) => {
        reportError.current?.(error);
        apply({ kind: "error", message: notifyErrorText(error), during: "enable" });
      })
      .finally(() => {
        if (inFlight.current === running) inFlight.current = null;
      });
    inFlight.current = running;
  }, [siteId, check]);

  useEffect(() => {
    recheck();
    const onVisible = () => {
      if (document.visibilityState === "visible") recheck();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) recheck(); // back from the bfcache
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onPageShow);
      opSeq.current++; // a late result is for the old site (or none)
      inFlight.current = null;
    };
  }, [recheck]);

  /** The registration from open's register(), or a new register() when there is
   * none or it failed: a failed one is forgotten, so the next Enable tries again
   * instead of awaiting the same rejected promise. */
  const registeredWorker = useCallback(async () => {
    const pending = registered.current ?? (registered.current = registerWorker());
    try {
      return await pending;
    } catch (error) {
      if (registered.current === pending) registered.current = null;
      throw error;
    }
  }, []);

  /** Call from a click handler only. */
  const enable = useCallback(async () => {
    if (!siteId) return;
    opSeq.current++;
    busy.current = true;
    setState({ kind: "asking" });
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState({ kind: permission === "denied" ? "blocked" : "off" });
        return;
      }
      // register's own registration (or its error), not navigator.serviceWorker
      // .ready: ready matches the document's creation URL, so it never settles
      // for a page that started outside /notify/ (Google sign-in's
      // /oauth/callback, /notify without the slash, an in-app navigation).
      const registration = await whenActive(await registeredWorker());
      const key = vapidKey.current ?? (vapidKey.current = await getVapidKey());
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        }));
      subId.current = await saveSubscription(siteId, subscription.toJSON());
      storeSubId(siteId, subId.current);
      setState({ kind: "on" });
    } catch (error) {
      reportError.current?.(error);
      setState({ kind: "error", message: notifyErrorText(error), during: "enable" });
    } finally {
      busy.current = false;
    }
  }, [siteId, registeredWorker]);

  /** Removes the server row, then the browser's subscription. A re-check that
   * is still running is waited for first, so its save can't land after the
   * delete (and the sub_id it gets back is the one deleted). */
  const disable = useCallback(async () => {
    if (!siteId) return;
    opSeq.current++;
    busy.current = true;
    try {
      await inFlight.current;
      const id = subId.current ?? storedSubId(siteId);
      if (id) await deleteSubscription(siteId, id);
      await unsubscribeBrowser();
      subId.current = null;
      storeSubId(siteId, null);
      setState({ kind: "off" });
    } catch (error) {
      reportError.current?.(error);
      setState({ kind: "error", message: TURN_OFF_FAILED, during: "disable" });
    } finally {
      busy.current = false;
    }
  }, [siteId]);

  return { state, enable, disable };
}

export type PushAlerts = ReturnType<typeof usePushAlerts>;
